export interface DraggableOptions {
  data?: any;
  effect?: DataTransfer["effectAllowed"];
  onDragStart?: (ev: DragEvent) => void;
  onDragEnd?: (ev: DragEvent) => void;
  dragClass?: string;
  targetGroups?: string[];
}

export interface DroppableOptions {
  accept?: (data: any, ev?: DragEvent) => boolean;
  onDrop?: (data: any, ev: DragEvent) => void;
  onDragEnter?: (ev: DragEvent) => void;
  onDragOver?: (ev: DragEvent) => void;
  onDragLeave?: (ev: DragEvent) => void;
  hoverClass?: string;
  transferTypes?: string[]; // typically transfer type use by the OS/Browser: "Files", "text/uri-list", "text/plain" etc.
  targetGroups?: string[];  // custom groups to match draggable targets
}

function setDataTransfer(ev: DragEvent, data: any, type = "application/json") {
  try {
    const payload = typeof data === "string" ? data : JSON.stringify(data);
    ev.dataTransfer?.setData(type, payload);
  } catch (e) {
    // ignore
  }
}

export const l3draggable = {
  bind(el: HTMLElement, binding: any) {
    const opts: DraggableOptions = binding.value || {};
    el.setAttribute("draggable", "true");

    const onDragStart = (ev: DragEvent) => {
      if (opts.dragClass) el.classList.add(opts.dragClass);
      if (opts.data) {
        const payload = JSON.stringify({ ...opts.data });
        setDataTransfer(ev, payload, "application/json");
      }
      if (opts.targetGroups) {
        // set custom type for target groups so we can identify compatible drop targets
        opts.targetGroups.forEach(group => {
          setDataTransfer(ev, "dragging", `application/x-${group}`);
        });
      }
      // set effectAllowed on dragstart (dropEffect is for the target during dragover)
      if (opts.effect && ev.dataTransfer) ev.dataTransfer.effectAllowed = opts.effect;
      if (opts.onDragStart) opts.onDragStart(ev);
    };

    const onDragEnd = (ev: DragEvent) => {
      if (opts.dragClass) el.classList.remove(opts.dragClass);
      if (opts.onDragEnd) opts.onDragEnd(ev);
    };

    // store handlers so we can remove later
    (el as any).__draggableHandlers = { onDragStart, onDragEnd };
    el.addEventListener("dragstart", onDragStart);
    el.addEventListener("dragend", onDragEnd);
  },
  unbind(el: HTMLElement) {
    const h = (el as any).__draggableHandlers;
    if (h) {
      el.removeEventListener("dragstart", h.onDragStart);
      el.removeEventListener("dragend", h.onDragEnd);
      delete (el as any).__draggableHandlers;
    }
    el.removeAttribute("draggable");
  }
};

export const l3droppable = {
  bind(el: HTMLElement, binding: any) {
    const opts: DroppableOptions = binding.value || {};
    let enterCounter = 0; // track nested enter/leave

    function parsePayload(ev: DragEvent) {
      try {
        // Check for JSON data
        const json = ev.dataTransfer?.getData("application/json");
        if (json) {
          return JSON.parse(json);
        }

        // Check for files
        // Some browsers/environments expose DataTransfer.items but leave files empty
        if (ev.dataTransfer?.items && ev.dataTransfer.items.length) {
          try {
            const itemsArr = Array.from(ev.dataTransfer.items as any);
            const filesFromItems = itemsArr.map((it: any) => {
              try {
                if (it && it.kind === 'file' && typeof it.getAsFile === 'function') {
                  return it.getAsFile();
                }
              } catch (e) {
                return null;
              }
              return null;
            }).filter((f: any) => !!f);
            if (filesFromItems && filesFromItems.length) {
              return filesFromItems;
            }
          } catch (e) {
            // ignore
          }
        }

        if (ev.dataTransfer?.files && ev.dataTransfer.files.length) {
          return Array.from(ev.dataTransfer.files);
        }

        // Check for plain text
        const plain = ev.dataTransfer?.getData("text/plain");
          if (plain) {
            try {
              return JSON.parse(plain);
            } catch {
              return plain;
            }
          }
      } catch (e) { /* ignore */ }
      return undefined;
    }

    function chooseDropEffect(ev: DragEvent, prefer = "move"): DataTransfer["dropEffect"] {
      const allowed = (ev.dataTransfer?.effectAllowed || "").toLowerCase();
      if (allowed.includes("copy")) return "copy";
      if (allowed.includes("move")) return "move";
      return prefer as DataTransfer["dropEffect"];
    }

    // minimal work on dragover (must call preventDefault to allow drop)
    const onDragOver = (ev: DragEvent) => {
      const payload = parsePayload(ev);
      const types = Array.from(ev.dataTransfer?.types ?? []);
      const transferTypes = opts.transferTypes || [];
      const targetGroups = opts.targetGroups || [];
      const hasTransferType = transferTypes.some(t => types.includes(t));
      const hasMatchingGroup = targetGroups.some(group => types.includes(`application/x-${group}`));
      const fallbackAccepted = hasTransferType || hasMatchingGroup || !!payload || Array.isArray(payload);
      const accepted = opts.accept ? (opts.accept(payload, ev) || fallbackAccepted) : true;
      // always preventDefault so drop is allowed (but we still may mark none)
      ev.preventDefault();
      if (!accepted) {
        if (ev.dataTransfer) {
          ev.dataTransfer.dropEffect = "none";
          ev.dataTransfer.effectAllowed = "none";
        }
        if (opts.onDragOver) opts.onDragOver(ev);
        return;
      }

      if (ev.dataTransfer) ev.dataTransfer.dropEffect = chooseDropEffect(ev, "copy");
      if (opts.onDragOver) opts.onDragOver(ev);
    };

    // show hover UI
    const onDragEnter = (ev: DragEvent) => {
      const payload = parsePayload(ev);
      const types = Array.from(ev.dataTransfer?.types ?? []);
      const transferTypes = opts.transferTypes || [];
      const targetGroups = opts.targetGroups || [];
      const hasTransferType = transferTypes.some(t => types.includes(t));
      const hasMatchingGroup = targetGroups.some(group => types.includes(`application/x-${group}`));
      const hasPayload = !!payload || Array.isArray(payload);
      const accepted = opts.accept ? (opts.accept(payload, ev) || hasTransferType || hasMatchingGroup || hasPayload) : true;
      if (!accepted) {
        ev.preventDefault();
        if (ev.dataTransfer) {
          ev.dataTransfer.dropEffect = "none";
          ev.dataTransfer.effectAllowed = "none";
        }
        if (opts.onDragEnter) opts.onDragEnter(ev);
        return;
      }

      ev.preventDefault();
      if (!hasMatchingGroup && !hasTransferType && !hasPayload) return;

      enterCounter++;
      // only add class on first enter
      if (enterCounter === 1 && opts.hoverClass) {
        el.classList.add(opts.hoverClass);
      }

      if (opts.onDragEnter) opts.onDragEnter(ev);
    };

    const onDragLeave = (ev: DragEvent) => {
      ev.preventDefault();
      enterCounter--;
      // only remove class when fully left
      if (enterCounter <= 0) {
        enterCounter = 0; // safety
        if (opts.hoverClass) el.classList.remove(opts.hoverClass);
      }
      if (opts.onDragLeave) opts.onDragLeave(ev);
    };

    // parse data only on actual drop and then call accept/onDrop
    const onDrop = (ev: DragEvent) => {
      ev.preventDefault();
      enterCounter = 0; // reset on drop
      if (opts.hoverClass) el.classList.remove(opts.hoverClass);

      let payload: any;
      try {
        payload = parsePayload(ev);
      } catch (e) {
        payload = undefined;
      }

      if (opts.accept && !opts.accept(payload, ev)) return;
      if (opts.onDrop) opts.onDrop(payload, ev);
    };

    (el as any).__droppableHandlers = { onDragOver, onDragEnter, onDragLeave, onDrop };
    // dragover and drop need passive: false for preventDefault() to work
    el.addEventListener("dragover", onDragOver, { passive: false });
    el.addEventListener("dragenter", onDragEnter);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop, { passive: false });
  },
  unbind(el: HTMLElement) {
    const h = (el as any).__droppableHandlers;
    if (h) {
      el.removeEventListener("dragover", h.onDragOver, { passive: false } as any);
      el.removeEventListener("dragenter", h.onDragEnter);
      el.removeEventListener("dragleave", h.onDragLeave);
      el.removeEventListener("drop", h.onDrop, { passive: false } as any);
      delete (el as any).__droppableHandlers;
    }
  }
};